import React, { useState } from 'react';
import { Stack, Group, Text, Badge, Divider, Collapse, UnstyledButton } from '@mantine/core';
import { ResponsiveDrawer } from '../UI/ResponsiveDrawer';
import {
  IconCalendarCog,
  IconWand,
  IconArrowsShuffle,
  IconListCheck,
  IconChefHat,
  IconChevronDown,
  IconPencil,
} from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';
import { MealPlanPreferences, MealPlanProfileSnapshot, PlanningMode } from '../../types/mealPlan';
import { useFeatureFlag } from '../../hooks/useFeatureFlags';
import { effectiveRotationSize } from '../../utils/rotationSize';

interface MealPlanPreferencesDrawerProps {
  opened: boolean;
  onClose: () => void;
  preferences: MealPlanPreferences;
  totalDays?: number;
  dateRange?: [string, string]; // [startDate, endDate] for calculating days of week
  profileSnapshot?: MealPlanProfileSnapshot; // Family profile as of generation time
}

// Matches the onboarding slider's mark labels (0/25/50/75/100)
const getAdventureLabel = (value: number): string => {
  if (value <= 12) return 'Safe';
  if (value <= 37) return 'Familiar';
  if (value <= 62) return 'Balanced';
  if (value <= 87) return 'Adventurous';
  return 'Bold';
};

// preferredCookingMethods are stored as snake_case tags (e.g. one_pot)
const humanizeTag = (tag: string): string => {
  const words = tag.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const SnapshotBadges = ({ items, color = 'gray' }: { items: string[]; color?: string }) => (
  <Group gap="xs">
    {items.map((item, index) => (
      <Badge key={index} variant="light" color={color} size="sm">
        {item}
      </Badge>
    ))}
  </Group>
);

export const MealPlanPreferencesDrawer: React.FC<MealPlanPreferencesDrawerProps> = ({
  opened,
  onClose,
  preferences,
  totalDays,
  dateRange,
  profileSnapshot
}) => {
  const [snapshotOpened, setSnapshotOpened] = useState(false);
  // Flag-on surfaces speak "batch cooking"; flag-off keeps "meal prep"
  const additionalMealsEnabled = useFeatureFlag('additional_meals');
  // Calculate take-your-time days (inverted logic from quick meal days)
  const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const getTakeYourTimeDays = (): string[] => {
    if (!dateRange) return [];

    const quickDays = preferences.quickMealDays;

    // Calculate ordered days from date range
    const [startDate, endDate] = dateRange;
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    const orderedDays = [];
    for (let i = 0; i < daysDiff; i++) {
      const currentDate = new Date(startYear, startMonth - 1, startDay + i);
      const dayOfWeek = currentDate.getDay();
      const dayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      orderedDays.push(DAYS_OF_WEEK[dayIndex]);
    }

    // If undefined, default to all days being quick (no take-your-time days)
    if (quickDays === undefined) return [];

    // If empty array, all days are take-your-time
    if (quickDays.length === 0) return orderedDays;

    // Otherwise, take-your-time days are those NOT in quickDays
    return orderedDays.filter(day => !quickDays.includes(day));
  };

  const takeYourTimeDays = getTakeYourTimeDays();

  // Mode summary, mirroring the wizard review step's language and color
  // coding (violet = new creations, teal = my recipes, blue = your picks).
  // Plans created before planningMode was persisted derive it from the
  // legacy recipeSources/locks contract.
  const lockedCount =
    (preferences.lockedRecipeIds?.length ?? 0) +
    (preferences.lockedCatalogRecipeIds?.length ?? 0);
  const mode: PlanningMode =
    preferences.planningMode ??
    (lockedCount > 0
      ? 'pick_a_few'
      : preferences.recipeSources === 'ai_only'
      ? 'surprise_me'
      : 'shuffle_cookbook');
  const noveltyDays = preferences.noveltyDays ?? preferences.aiRecipeDays ?? 0;
  const penciledMeals = preferences.penciledMeals ?? [];
  // Penciled nights were covered by the family, not filled by generation
  const mealsNeeded =
    preferences.uniqueMeals ??
    Math.max(0, (totalDays ?? 0) - penciledMeals.length);
  const fullyPenciled =
    (totalDays ?? 0) > 0 && penciledMeals.length >= (totalDays ?? 0);
  // Penciled nights aren't cooked from recipes; meal prep covers the rest
  const cookingDays = Math.max(0, (totalDays ?? 0) - penciledMeals.length);

  // Preferred cooking days govern ALL batch cooking (batch dinners,
  // leftover-feeding dinners, make-ahead rotations), so the row renders in
  // whichever relevant section exists: Batch cooking when shown, else the
  // Breakfast & lunch section. Concrete dates like the wizard's chips (a
  // Sunday before the plan starts is a real option).
  const batchSectionShown =
    preferences.uniqueMeals !== undefined &&
    !!totalDays &&
    preferences.uniqueMeals < cookingDays;
  const formatPrepDate = (dateString: string): string => {
    const [y, m, d] = dateString.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${m}/${d}`;
  };

  const getModeSummary = (): {
    icon: React.ReactNode;
    label: string;
    detail: string;
    color: string;
  } => {
    switch (mode) {
      case 'shuffle_cookbook':
        return {
          icon: <IconArrowsShuffle size={14} />,
          label: 'Shuffle my recipes',
          color: 'teal',
          detail:
            noveltyDays > 0
              ? `A rotation from your cookbook, plus ${noveltyDays} new idea${
                  noveltyDays !== 1 ? 's' : ''
                } sprinkled in`
              : 'A rotation from your cookbook, no surprises',
        };
      case 'pick_a_few':
        return {
          icon: <IconListCheck size={14} />,
          label: 'Your picks',
          color: 'blue',
          detail:
            lockedCount > 0
              ? `${lockedCount} meal${lockedCount !== 1 ? 's' : ''} you picked${
                  mealsNeeded - lockedCount > 0
                    ? `. We filled the other ${mealsNeeded - lockedCount}`
                    : ''
                }`
              : 'Built from your picks',
        };
      case 'surprise_me':
      default:
        return {
          icon: <IconWand size={14} />,
          label: 'Surprise me',
          color: 'violet',
          detail: "We picked every meal for you, tailored to your family's tastes",
        };
    }
  };
  // A fully penciled plan generated nothing; the derived mode's copy ("We
  // picked every meal for you") would be false
  const modeSummary = fullyPenciled
    ? {
        icon: <IconPencil size={14} />,
        label: 'Penciled in by you',
        color: 'grape',
        detail: 'Every night was covered by your own meals.',
      }
    : getModeSummary();
  const PreferenceRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Group gap="md" align="flex-start">
      <Text size="sm" fw={500} c="dark" style={{ minWidth: '140px' }}>
        {label}
      </Text>
      <div style={{ flex: 1 }}>{children}</div>
    </Group>
  );

  const DaysBadge = ({ days }: { days: string[] }) => (
    <Group gap="xs">
      {days.map((day, index) => (
        <Badge key={index} variant="light" color="gray" size="sm">
          {day}
        </Badge>
      ))}
    </Group>
  );

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      title={
        <DrawerHeader
          icon={<IconCalendarCog size={18} />}
          title="Meal plan preferences"
        />
      }
      size="md"
    >
      <Stack gap="lg">
        {/* Basic Preferences */}
        <Stack gap="md">
          <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
            General settings
          </Text>
          
          <PreferenceRow label="How it was built">
            <Stack gap="xs">
              <Badge
                variant="light"
                color={modeSummary.color}
                size="md"
                leftSection={modeSummary.icon}
              >
                {modeSummary.label}
              </Badge>
              <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                {modeSummary.detail}
              </Text>
            </Stack>
          </PreferenceRow>

        </Stack>

        {/* Flag ON: one section per meal, mirroring the wizard's and the
            profile's per-meal organization (Dinners, Breakfasts, Lunches),
            with preferred cooking days in their own cross-meal block, same
            as the wizard keeps prep chips outside any single meal's row.
            Flag OFF keeps the original Meal prep section. */}
        {additionalMealsEnabled ? (
          <>
            {preferences.additionalMeals?.breakfast && (
              <>
                <Divider />
                <Stack gap="md">
                  <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                    Breakfasts
                  </Text>
                  <PreferenceRow label="Style">
                    <Stack gap="xs">
                      <Text size="sm" c="dark" style={{ lineHeight: 1.4 }}>
                        {(() => {
                          if (!totalDays) return 'A rotation of quick, repeatable breakfasts';
                          const n = effectiveRotationSize(
                            'breakfast',
                            totalDays,
                            preferences.additionalMeals?.breakfast?.rotationSize
                          );
                          return n === 1
                            ? 'One quick, repeatable breakfast'
                            : `A rotation of ${n} quick, repeatable breakfasts`;
                        })()}
                      </Text>
                      <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                        {preferences.additionalMeals.breakfast.batch !== false
                          ? 'Made ahead in batches'
                          : 'Cooked fresh each morning'}
                      </Text>
                    </Stack>
                  </PreferenceRow>
                </Stack>
              </>
            )}

            {preferences.additionalMeals?.lunch && (
              <>
                <Divider />
                <Stack gap="md">
                  <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                    Lunches
                  </Text>
                  <PreferenceRow label="Style">
                    <Stack gap="xs">
                      <Text size="sm" c="dark" style={{ lineHeight: 1.4 }}>
                        {(() => {
                          if (preferences.additionalMeals?.lunch?.mode === 'leftovers') {
                            return 'Leftovers from the night before';
                          }
                          if (!totalDays) return 'A rotation of easy, packable lunches';
                          const n = effectiveRotationSize(
                            'lunch',
                            totalDays,
                            preferences.additionalMeals?.lunch?.rotationSize
                          );
                          return n === 1
                            ? 'One easy, packable lunch'
                            : `A rotation of ${n} easy, packable lunches`;
                        })()}
                      </Text>
                      <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                        {preferences.additionalMeals.lunch.mode === 'leftovers'
                          ? "Dinners are sized up so leftovers cover the next day's lunch."
                          : preferences.additionalMeals.lunch.batch !== false
                            ? 'Made ahead in batches'
                            : 'Made fresh each day'}
                      </Text>
                    </Stack>
                  </PreferenceRow>
                </Stack>
              </>
            )}

            <Divider />
            <Stack gap="md">
              <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                Dinners
              </Text>
              <PreferenceRow label="Cooking schedule">
                {batchSectionShown ? (
                  <Stack gap="xs">
                    <Text size="sm" c="dark" style={{ lineHeight: 1.4 }}>
                      Cook {preferences.uniqueMeals} dinner{preferences.uniqueMeals !== 1 ? 's' : ''} covering {cookingDays} night{cookingDays !== 1 ? 's' : ''}
                    </Text>
                    <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                      {cookingDays - (preferences.uniqueMeals ?? 0)} night{cookingDays - (preferences.uniqueMeals ?? 0) !== 1 ? 's' : ''} will use leftovers
                    </Text>
                  </Stack>
                ) : (
                  <Text size="sm" c="dark" style={{ lineHeight: 1.4 }}>
                    A fresh dinner every night
                  </Text>
                )}
              </PreferenceRow>
              {preferences.mealPrepPreferences?.generalPreferences && (
                <PreferenceRow label="Batch cooking notes">
                  <Text size="sm" c="dark" style={{ fontStyle: 'italic', lineHeight: 1.4 }}>
                    "{preferences.mealPrepPreferences.generalPreferences}"
                  </Text>
                </PreferenceRow>
              )}
            </Stack>

            {(preferences.prepDays?.length ?? 0) > 0 && (
              <>
                <Divider />
                <Stack gap="md">
                  <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                    Preferred cooking days
                  </Text>
                  <Group gap={6}>
                    {preferences.prepDays!.map(d => (
                      <Badge key={d} variant="light" color="gray" size="sm">
                        {formatPrepDate(d)}
                      </Badge>
                    ))}
                  </Group>
                  <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                    Batch cooking lands on these days whenever it keeps food fresh.
                  </Text>
                </Stack>
              </>
            )}
          </>
        ) : (
          <>
            {/* Meal Prep Settings */}
            {batchSectionShown && (
              <>
                <Divider />
                <Stack gap="md">
                  <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                    Meal prep
                  </Text>

                  <PreferenceRow label="Cooking schedule">
                    <Stack gap="xs">
                      <Group gap="xs" align="flex-start" wrap="nowrap">
                        <IconChefHat size={16} color="var(--mantine-color-orange-6)" />
                        <Text size="sm" c="dark" style={{ lineHeight: 1.4 }}>
                          Cook {preferences.uniqueMeals} meal{preferences.uniqueMeals !== 1 ? 's' : ''} for {cookingDays} day{cookingDays !== 1 ? 's' : ''}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                        {cookingDays - (preferences.uniqueMeals ?? 0)} day{cookingDays - (preferences.uniqueMeals ?? 0) !== 1 ? 's' : ''} will use prepped leftovers
                      </Text>
                    </Stack>
                  </PreferenceRow>

                  {preferences.mealPrepPreferences?.generalPreferences && (
                    <PreferenceRow label="Meal prep preferences">
                      <Text size="sm" c="dark" style={{ fontStyle: 'italic', lineHeight: 1.4 }}>
                        "{preferences.mealPrepPreferences.generalPreferences}"
                      </Text>
                    </PreferenceRow>
                  )}
                </Stack>
              </>
            )}

            {/* Breakfast/lunch coverage beyond the dinner baseline (slot
                plans render for flag-off viewers too; the flag gates
                creation only) */}
            {preferences.additionalMeals && (
              <>
                <Divider />
                <Stack gap="md">
                  <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                    Breakfast & lunch
                  </Text>

                  {preferences.additionalMeals.breakfast && (
                    <PreferenceRow label="Breakfasts">
                      <Text size="sm" c="dark" style={{ lineHeight: 1.4 }}>
                        A rotation of quick, repeatable breakfasts
                      </Text>
                    </PreferenceRow>
                  )}

                  {preferences.additionalMeals.lunch && (
                    <PreferenceRow label="Lunches">
                      <Stack gap="xs">
                        <Text size="sm" c="dark" style={{ lineHeight: 1.4 }}>
                          {preferences.additionalMeals.lunch.mode === 'leftovers'
                            ? 'Leftovers from the night before'
                            : 'A rotation of easy, packable lunches'}
                        </Text>
                        {preferences.additionalMeals.lunch.mode === 'leftovers' && (
                          <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                            Dinners are sized up so leftovers cover the next day's lunch.
                          </Text>
                        )}
                      </Stack>
                    </PreferenceRow>
                  )}
                </Stack>
              </>
            )}
          </>
        )}

        {/* Schedule Preferences */}
        {(takeYourTimeDays.length > 0 ||
          penciledMeals.length > 0 ||
          (preferences.events && preferences.events.length > 0)) && (
          <>
            <Divider />
            <Stack gap="md">
              <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                Schedule
              </Text>

              {penciledMeals.length > 0 && (
                <PreferenceRow label="Your nights">
                  <Stack gap="xs">
                    {penciledMeals.map((meal) => (
                      <Group key={meal.day} gap="xs" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                        <Badge variant="light" color="grape" size="sm" w={96} style={{ flexShrink: 0 }}>
                          {meal.day}
                        </Badge>
                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" c="dark" fw={500}>
                            {meal.name}
                          </Text>
                          {meal.ingredients.length > 0 && (
                            <Text size="xs" c="dimmed">
                              {meal.ingredients.length} ingredient{meal.ingredients.length !== 1 ? 's' : ''} on the grocery list
                            </Text>
                          )}
                          {meal.note && (
                            <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                              "{meal.note}"
                            </Text>
                          )}
                        </Stack>
                      </Group>
                    ))}
                  </Stack>
                </PreferenceRow>
              )}

              {takeYourTimeDays.length > 0 && (
                <PreferenceRow label={additionalMealsEnabled ? 'Take-your-time nights' : 'Take-your-time days'}>
                  <DaysBadge days={takeYourTimeDays} />
                </PreferenceRow>
              )}
              
              {preferences.events && preferences.events.length > 0 && (
                <PreferenceRow label="Guests & occasions">
                  <Stack gap="xs">
                    {preferences.events.map((event, index) => (
                      <Group key={index} gap="xs" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
                        <Badge variant="light" color="gray" size="sm" w={96} style={{ flexShrink: 0 }}>
                          {event.day}
                        </Badge>
                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                          {event.occasion && (
                            <Text size="sm" c="dark" fw={500}>
                              {event.occasion}
                            </Text>
                          )}
                          {event.guestCount && (
                            <Text size="xs" c="dimmed">
                              +{event.guestCount} guest{event.guestCount !== 1 ? 's' : ''}
                            </Text>
                          )}
                          {event.specialRequests && (
                            <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                              "{event.specialRequests}"
                            </Text>
                          )}
                        </Stack>
                      </Group>
                    ))}
                  </Stack>
                </PreferenceRow>
              )}
            </Stack>
          </>
        )}
        
        {/* Options */}
        {(preferences.customRequests || preferences.usePantryIngredients) && (
          <>
            <Divider />
            <Stack gap="md">
              <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                This week's extras
              </Text>
              
              {preferences.usePantryIngredients && (
                <PreferenceRow label="Pantry items">
                  <Group gap="xs" align="flex-start" wrap="nowrap">
                    <Text size="sm" c="green.7" style={{ lineHeight: 1.4 }}>✓</Text>
                    <Text size="sm" c="dark" style={{ lineHeight: 1.4 }}>Use pantry items when planning meals</Text>
                  </Group>
                </PreferenceRow>
              )}
              
              {preferences.customRequests && (
                <PreferenceRow label="Preferences & requests">
                  <Text size="sm" c="dark" style={{ fontStyle: 'italic' }}>
                    "{preferences.customRequests}"
                  </Text>
                </PreferenceRow>
              )}
            </Stack>
          </>
        )}

        {/* Profile snapshot: the family profile as it stood at generation
            time, so the plan keeps its original context even after profile
            settings change. Collapsed by default; absent on older plans. */}
        {profileSnapshot && (
          <>
            <Divider />
            <Stack gap="md">
              <UnstyledButton onClick={() => setSnapshotOpened((o) => !o)}>
                <Group gap="md" justify="space-between" wrap="nowrap">
                  <Stack gap={2}>
                    <Text size="sm" fw={600} c="dark" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                      Your profile at the time
                    </Text>
                    <Text size="xs" c="dimmed">
                      The settings this plan was built with. Your profile may have changed since.
                    </Text>
                  </Stack>
                  <IconChevronDown
                    size={16}
                    style={{
                      flexShrink: 0,
                      color: 'var(--mantine-color-gray-6)',
                      transform: snapshotOpened ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 150ms ease',
                    }}
                  />
                </Group>
              </UnstyledButton>

              <Collapse in={snapshotOpened}>
                <Stack gap="md">
                  {(profileSnapshot.adults !== undefined || profileSnapshot.kids !== undefined) && (
                    <PreferenceRow label="Household">
                      <Text size="sm" c="dark">
                        {[
                          profileSnapshot.adults !== undefined
                            ? `${profileSnapshot.adults} adult${profileSnapshot.adults !== 1 ? 's' : ''}`
                            : undefined,
                          profileSnapshot.kids
                            ? `${profileSnapshot.kids} kid${profileSnapshot.kids !== 1 ? 's' : ''}`
                            : undefined,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        {profileSnapshot.includeLeftovers ? ' · cooking extra for leftovers' : ''}
                      </Text>
                    </PreferenceRow>
                  )}

                  {profileSnapshot.cookingSkill && (
                    <PreferenceRow label="Cooking skill">
                      <Text size="sm" c="dark" tt="capitalize">
                        {profileSnapshot.cookingSkill}
                      </Text>
                    </PreferenceRow>
                  )}

                  {profileSnapshot.cuisineAdventure !== undefined && (
                    <PreferenceRow label="Cuisine adventure">
                      <Text size="sm" c="dark">
                        {getAdventureLabel(profileSnapshot.cuisineAdventure)}
                      </Text>
                    </PreferenceRow>
                  )}

                  {(profileSnapshot.dietaryRestrictions?.length ?? 0) > 0 && (
                    <PreferenceRow label="Dietary restrictions">
                      <SnapshotBadges items={profileSnapshot.dietaryRestrictions!} />
                    </PreferenceRow>
                  )}

                  {(profileSnapshot.allergies?.length ?? 0) > 0 && (
                    <PreferenceRow label="Allergies">
                      <SnapshotBadges items={profileSnapshot.allergies!} color="red" />
                    </PreferenceRow>
                  )}

                  {(profileSnapshot.dislikes?.length ?? 0) > 0 && (
                    <PreferenceRow label="Dislikes">
                      <SnapshotBadges items={profileSnapshot.dislikes!} />
                    </PreferenceRow>
                  )}

                  {(profileSnapshot.cuisinePreferences?.length ?? 0) > 0 && (
                    <PreferenceRow label="Favorite cuisines">
                      <SnapshotBadges items={profileSnapshot.cuisinePreferences!} />
                    </PreferenceRow>
                  )}

                  {(profileSnapshot.avoidCuisines?.length ?? 0) > 0 && (
                    <PreferenceRow label="Avoided cuisines">
                      <SnapshotBadges items={profileSnapshot.avoidCuisines!} />
                    </PreferenceRow>
                  )}

                  {(profileSnapshot.favoriteProteins?.length ?? 0) > 0 && (
                    <PreferenceRow label="Favorite proteins">
                      <SnapshotBadges items={profileSnapshot.favoriteProteins!} />
                    </PreferenceRow>
                  )}

                  {(profileSnapshot.avoidProteins?.length ?? 0) > 0 && (
                    <PreferenceRow label="Avoided proteins">
                      <SnapshotBadges items={profileSnapshot.avoidProteins!} />
                    </PreferenceRow>
                  )}

                  {(profileSnapshot.preferredCookingMethods?.length ?? 0) > 0 && (
                    <PreferenceRow label="Cooking methods">
                      <SnapshotBadges
                        items={profileSnapshot.preferredCookingMethods!.map(humanizeTag)}
                      />
                    </PreferenceRow>
                  )}

                  {profileSnapshot.flavorProfile && (
                    <PreferenceRow label="Flavor profile">
                      <Text size="sm" c="dark" style={{ fontStyle: 'italic', lineHeight: 1.4 }}>
                        "{profileSnapshot.flavorProfile}"
                      </Text>
                    </PreferenceRow>
                  )}
                </Stack>
              </Collapse>
            </Stack>
          </>
        )}

      </Stack>
    </ResponsiveDrawer>
  );
};
